import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from '@/auth/AuthContext';
import type { OrgMembershipView } from '@/api/types';

interface OrgContextValue {
  organizations: OrgMembershipView[];
  currentOrgId: string | null;
  currentOrg: OrgMembershipView | null;
  setCurrentOrgId: (id: string) => void;
  hasOrgs: boolean;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

const STORAGE_KEY = 'aima.currentOrgId';

export function OrgProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const orgs = user?.organizations ?? [];

  // `explicit` is the user's switcher pick (or stale stored choice). The effective `currentOrgId`
  // is derived synchronously: explicit-if-valid, else first available. This avoids a first-render
  // window where currentOrgId is null while a useEffect catches up — which would crash any
  // route using useRequireOrg.
  const [explicitOrgId, setExplicitOrgIdState] = useState<string | null>(() => {
    return localStorage.getItem(STORAGE_KEY);
  });

  const currentOrgId = useMemo(() => {
    if (explicitOrgId && orgs.some(o => o.id === explicitOrgId)) return explicitOrgId;
    return orgs[0]?.id ?? null;
  }, [orgs, explicitOrgId]);

  useEffect(() => {
    if (currentOrgId) {
      localStorage.setItem(STORAGE_KEY, currentOrgId);
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [currentOrgId]);

  const setCurrentOrgId = useCallback((id: string) => {
    setExplicitOrgIdState(id);
  }, []);

  const value = useMemo<OrgContextValue>(() => {
    const currentOrg = orgs.find(o => o.id === currentOrgId) ?? null;
    return {
      organizations: orgs,
      currentOrgId,
      currentOrg,
      setCurrentOrgId,
      hasOrgs: orgs.length > 0,
    };
  }, [orgs, currentOrgId, setCurrentOrgId]);

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useOrg(): OrgContextValue {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used inside <OrgProvider>');
  return ctx;
}

/** For routes/components that *require* a current org — never returns null. */
export function useRequireOrg(): { id: string; org: OrgMembershipView } {
  const { currentOrgId, currentOrg } = useOrg();
  if (!currentOrgId || !currentOrg) {
    throw new Error('No organization selected');
  }
  return { id: currentOrgId, org: currentOrg };
}
